import {
  Box,
  Flex,
  Icon,
  IconButton,
  Image,
  SimpleGrid,
  Spinner,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { LayoutGrid, Star } from 'lucide-react';
import React, { useState } from 'react';
import { LuMinus, LuPlus } from 'react-icons/lu';
import { useApplications } from '../../services/hooks/useApplications';
import iflexApi from '../../services/iflexApi';
import getBuildFormUrlencodedParams from '../../utils/getBuildFormUrlencodedParams';

interface IServiceItem {
  id: string;
  styles?: any;
  textStyles?: any;
  nome: string;
  icon: any;
  isFavorited?: boolean;
  action?: () => void;
}

const Applications = () => {
  // estilos
  const bg = useColorModeValue('white', 'gray.800');
  const details = useColorModeValue('gray.100', 'gray.900');
  const color = useColorModeValue('gray.700', 'white');

  // estados
  const [selectingFavorites, setSelectingFavorites] = useState(false);

  // dados
  const {
    data: dataApplications,
    isLoading: isLoadingApps,
    refetch,
  } = useApplications();

  const HandleRedirect = (link: string) => {
    // ir para o sistema
    if (!link) return;
    window.location.href = link;
  };

  const HandleFavorite = async (id_siu: string) => {
    // favoritar sistema
    const data = getBuildFormUrlencodedParams({
      action: 'alternaFavoritoSistema',
      controller: 'Integrations/Analytics/Applications',
      id_siu,
      favoritar: true,
    });

    // await iflexApi.post('', data);

    refetch();
    setSelectingFavorites(false);
  };

  const HandleUnFavorite = async (id_siu: string) => {
    // desfavoritar sistema
    const data = getBuildFormUrlencodedParams({
      action: 'alternaFavoritoSistema',
      controller: 'Integrations/Analytics/Applications',
      id_siu,
      favoritar: false,
    });

    // await iflexApi.post('', data);
    refetch();
  };

  const ServiceItem: React.FC<IServiceItem> = ({
    styles,
    textStyles,
    nome,
    icon,
    isFavorited,
    action,
    id,
  }) => {
    return (
      <Flex
        role="group"
        flexDir="column"
        w="100%"
        _hover={{ bg }}
        cursor="pointer"
        rounded="2xl"
        p="2"
        {...styles}
        position="relative"
        onClick={action ?? undefined}
      >
        <IconButton
          _groupHover={{
            display: isFavorited ? 'flex' : 'none',
          }}
          display="none"
          as={LuMinus}
          aria-label="Remover favorito"
          position="absolute"
          top="-3px"
          right="-3px"
          boxSize="20px"
          rounded="sm"
          minW="unset"
          colorScheme="red"
          onClick={e => {
            e.stopPropagation();
            HandleUnFavorite(id);
          }}
        />
        <Image src={`${icon}`} rounded="2xl" boxShadow="md" />
        <Text {...textStyles} textAlign="center" mt="1">
          {nome}
        </Text>
      </Flex>
    );
  };

  const Favorites = () => {
    return (
      <>
        <Flex
          bg={details}
          roundedTop="2xl"
          justify="space-between"
          align="center"
          pt="4"
          px="4"
        >
          <Flex>
            <Icon as={Star} boxSize="24px" color="yellow.500" />
            <Text pl="1">Favoritos</Text>
          </Flex>
          <IconButton
            as={LuPlus}
            boxSize="24px"
            bg="none"
            _hover={{ bg: 'none' }}
            color={color}
            aria-label="teste"
            cursor="pointer"
            onClick={() => setSelectingFavorites(true)}
          />
        </Flex>
        <SimpleGrid
          columns={3}
          spacingX="4"
          maxH="25vh"
          overflowY="auto"
          px="4"
          py="2"
          bg={details}
          roundedBottom="2xl"
          sx={{
            '&::-webkit-scrollbar': {
              width: '7px',
              background: 'none',
            },
            '&::-webkit-scrollbar-track': {
              background: 'none',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'gray.500',
              borderRadius: '10px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'gray.400',
            },
          }}
        >
          {dataApplications?.favoritos?.map(fav => {
            const app = dataApplications.sistemas.find(
              sis => sis.id_sis === fav.sistema_siu,
            );
            if (!app) return null;
            return (
              <ServiceItem
                id={app.id_sis}
                nome={app.nome_sis}
                icon={app?.icone_sis}
                action={() => HandleRedirect(app.url_sis)}
                isFavorited
              />
            );
          })}
          {isLoadingApps && (
            <Flex colSpan={6} justify="center" align="center" w="350%" h="100%">
              <Spinner color="blue.500" />
            </Flex>
          )}
        </SimpleGrid>
      </>
    );
  };

  const AllApps = () => {
    return (
      <>
        <Flex mt="3" bg={details} pt="4" pl="4" roundedTop="2xl">
          <Icon as={LayoutGrid} boxSize="24px" />
          <Text pl="1">Todos</Text>
        </Flex>
        <SimpleGrid
          columns={3}
          spacingX="4"
          spacinfgY="2"
          maxH="32vh"
          overflowY="auto"
          px="4"
          py="2"
          bg={details}
          roundedBottom="2xl"
          sx={{
            '&::-webkit-scrollbar': {
              width: '7px',
              background: 'none',
            },
            '&::-webkit-scrollbar-track': {
              background: 'none',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'gray.500',
              borderRadius: '10px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'gray.400',
            },
          }}
        >
          {dataApplications?.sistemas?.map(app => {
            return (
              <ServiceItem
                id={app?.id_sis}
                nome={app?.nome_sis}
                icon={app?.icone_sis}
                action={() => HandleRedirect(app.url_sis)}
              />
            );
          })}
          {isLoadingApps && (
            <Flex colSpan={3} justify="center" align="center" w="350%" h="100%">
              <Spinner color="blue.500" />
            </Flex>
          )}
        </SimpleGrid>
      </>
    );
  };

  const SelectFavorite = () => {
    return (
      <Box>
        <Flex mt="3" bg={details} pt="4" pl="4" roundedTop="2xl">
          <Icon as={Star} boxSize="24px" color="yellow.500" />
          <Text pl="1">Selecione para favoritar</Text>
        </Flex>
        <SimpleGrid
          columns={3}
          spacingX="4"
          spacinfgY="2"
          maxH="50vh"
          overflowY="auto"
          px="4"
          py="2"
          bg={details}
          roundedBottom="2xl"
          sx={{
            '&::-webkit-scrollbar': {
              width: '7px',
              background: 'none',
            },
            '&::-webkit-scrollbar-track': {
              background: 'none',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'gray.500',
              borderRadius: '10px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'gray.400',
            },
          }}
        >
          {dataApplications?.sistemas?.map(app => {
            return (
              <ServiceItem
                id={app.id_sis}
                action={() => HandleFavorite(app.id_sis)}
                nome={app.nome_sis}
                icon={app?.icone_sis}
                textStyles={{
                  _groupHover: {
                    color,
                  },
                }}
                styles={{
                  _hover: {
                    bgGradient:
                      'linear(to-b, rgba(255, 231, 97, 0.6) 40%, rgba(255, 231, 97, 0.3) 80%)',
                  },
                }}
              />
            );
          })}
        </SimpleGrid>
      </Box>
    );
  };

  return (
    <Flex flexDir="column" onClick={e => e.stopPropagation()} w="100%">
      {!selectingFavorites && (
        <>
          <Favorites />
          <AllApps />
        </>
      )}
      {selectingFavorites && (
        <>
          <SelectFavorite />
        </>
      )}
    </Flex>
  );
};

export default Applications;
